import { btnHtml, ribbonHtml } from "./kit";

/**
 * Add to Home Screen card (engagement doc §A5): a Safari tab loses its storage after 7 days without a visit, so the
 * save lives only as long as the player keeps coming back; a home-screen app keeps it (and is what iOS Web Push needs
 * later). Shown on the hub after a win, never in a level, at most once a week, never once installed or declined.
 * The steps are drawn with plain DOM/SVG glyphs, not screenshots of Apple's UI.
 */
export const INSTALL_CARD_INTERVAL_SECONDS = 7 * 24 * 3600;

export interface InstallCardState {
  readonly now: number;
  /** When the card was last shown (clock seconds), null if never. */
  readonly lastShown: number | null;
  /** "Don't ask again" was tapped. */
  readonly declined: boolean;
  /** Already running from the home screen. */
  readonly standalone: boolean;
  /** At least one level won (the card waits for the first win: the save is worth keeping by then). */
  readonly hasWon: boolean;
}

export function installCardDue(s: InstallCardState): boolean {
  return s.hasWon && !s.standalone && !s.declined && (s.lastShown === null || s.now - s.lastShown >= INSTALL_CARD_INTERVAL_SECONDS);
}

export type InstallChoice = "later" | "never" | "installed";

/** Share: a square tray with an arrow out of it (drawn here, the way the glyph reads, not Apple's artwork). */
const SHARE_SVG = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 9H6.5A1.5 1.5 0 0 0 5 10.5v9A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 17.5 9H16"/><path d="M12 3v12M8 6.5 12 3l4 3.5"/></svg>`;
/** Add: a rounded square with a plus. */
const ADD_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3.5" y="3.5" width="17" height="17" rx="4"/><path d="M12 8v8M8 12h8"/></svg>`;
/** A browser's overflow menu (three dots). */
const MENU_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>`;

function appleSteps(): string {
  return `<ol class="isteps">
    <li><span class="n">1</span><span class="mock bar"><i></i><i></i><b class="hot">${SHARE_SVG}</b><i></i><i></i></span><small>Tap <b>Share</b> in Safari's toolbar (or under <b>···</b>)</small></li>
    <li><span class="n">2</span><span class="mock row">${ADD_SVG}<span>Add to Home Screen</span></span><small>Scroll the list and choose it</small></li>
    <li><span class="n">3</span><span class="mock pill">Add</span><small>Then open the game from its icon</small></li>
  </ol>`;
}

function otherSteps(): string {
  return `<ol class="isteps">
    <li><span class="n">1</span><span class="mock bar"><i></i><i></i><i></i><i></i><b class="hot">${MENU_SVG}</b></span><small>Open the browser menu</small></li>
    <li><span class="n">2</span><span class="mock row">${ADD_SVG}<span>Add to Home screen</span></span><small>or <b>Install app</b></small></li>
  </ol>`;
}

/**
 * The card. `prompt` is the browser's own install dialog where it offers one (Chromium's beforeinstallprompt); then
 * INSTALL calls it instead of the steps. Resolves with what the player chose.
 */
export function showInstallCard(parent: HTMLElement, o: { apple: boolean; prompt: (() => Promise<boolean>) | null }): Promise<InstallChoice> {
  return new Promise((resolve) => {
    const scrim = document.createElement("div");
    scrim.className = "scrim installscrim";
    scrim.innerHTML = `<div class="panel dialog installcard">${ribbonHtml("KEEP YOUR PROGRESS", "", "dtitle disp")}
      <p class="lead">Add to Home Screen to keep your progress and get upgrade alerts.</p>
      ${o.prompt ? "" : o.apple ? appleSteps() : otherSteps()}
      <div class="row two">${btnHtml("NOT NOW", "dark", { className: "later disp" })}${o.prompt ? btnHtml("INSTALL", "gold", { className: "install disp accent" }) : btnHtml("GOT IT", "gold", { className: "later2 disp accent" })}</div>
      <button class="never">Don't ask again</button></div>`;
    const done = (c: InstallChoice) => { scrim.remove(); resolve(c); };
    scrim.querySelector<HTMLButtonElement>(".later")!.onclick = () => done("later");
    scrim.querySelector<HTMLButtonElement>(".later2")?.addEventListener("click", () => done("later"));
    scrim.querySelector<HTMLButtonElement>(".never")!.onclick = () => done("never");
    scrim.querySelector<HTMLButtonElement>(".install")?.addEventListener("click", () => { void o.prompt?.().then((ok) => done(ok ? "installed" : "later")); });
    parent.append(scrim);
  });
}
